/**
 * Download progress of the 3D room (glTF rooms, props, panoramas), keyed by the
 * spec URL, plus a flag for "the 3D canvas exists" (procedural rooms have
 * nothing to download). Read by the intro card's loading bar.
 *
 * Progress events arrive per network chunk; listeners are notified at most
 * every 100 ms so the bar never forces a render per chunk.
 */
type Entry = { loaded: number; total: number; done: boolean };

const entries = new Map<string, Entry>();
const listeners = new Set<() => void>();
let sceneReady = false;
let version = 0;
let timer: ReturnType<typeof setTimeout> | null = null;

function emitNow() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  version++;
  listeners.forEach((fn) => fn());
}

function emitSoon() {
  if (timer) return;
  timer = setTimeout(emitNow, 100);
}

export function reportLoadProgress(key: string, loaded: number, total: number) {
  const prev = entries.get(key);
  if (prev?.done) return;
  entries.set(key, { loaded, total: total || prev?.total || 0, done: false });
  emitSoon();
}

export function markLoadDone(key: string) {
  const prev = entries.get(key);
  if (prev?.done) return;
  const total = prev?.total || prev?.loaded || 0;
  entries.set(key, { loaded: total, total, done: true });
  emitNow();
}

export function markSceneReady() {
  if (sceneReady) return;
  sceneReady = true;
  emitNow();
}

export function isSceneReady() {
  return sceneReady;
}

export function subscribeLoadProgress(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function getLoadVersion() {
  return version;
}

/** Byte totals across `keys`; `total` is 0 when no size is known yet. */
export function summarizeLoad(keys: string[]) {
  let loaded = 0;
  let total = 0;
  let started = false;
  let allDone = keys.length > 0;
  for (const k of keys) {
    const e = entries.get(k);
    if (!e) {
      allDone = false;
      continue;
    }
    started = true;
    if (!e.done) allDone = false;
    if (e.total > 0) {
      total += e.total;
      loaded += Math.min(e.loaded, e.total);
    }
  }
  return { loaded, total, started, allDone };
}

/** fetch() with progress reporting; resolves to an object URL of the body. */
export async function fetchWithProgress(url: string): Promise<string> {
  const res = await fetch(url, { credentials: "same-origin" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const total = Number(res.headers.get("content-length")) || 0;
  if (!res.body) {
    const blob = await res.blob();
    markLoadDone(url);
    return URL.createObjectURL(blob);
  }
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  reportLoadProgress(url, 0, total);
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      loaded += value.byteLength;
      reportLoadProgress(url, loaded, total);
    }
  }
  const blob = new Blob(chunks as BlobPart[], { type: res.headers.get("content-type") || "" });
  return URL.createObjectURL(blob);
}
